<template>
  <block-item-card
    v-if="netInterestIncomeSensitivity"
    class="fill-height"
    :title="$t('Rates.Section.NetInterestIncomeSensitivity.Title')"
    :tooltip="$t('Rates.Section.NetInterestIncomeSensitivity.NetInterestIncomeSensitivity')"
  >
    <v-row
      class="ma-0 py-4 px-2"
      no-gutters
    >
      <v-col
        cols="12"
        class="rounded-lg"
      >
        <div class="chart-container">
          <div class="chart-metric">
            <div
              class="formula"
              :style="`--const:'${sofrFactor}%'; --coef:'${slopeFactorAbs}%';` "
            >
              <span class="sym">NII</span><sub>t</sub>
              <span>=</span>
              <span class="var-const" />
              <span>&times;</span>
              <span class="sym">{{ niiAssetLabel }}</span>
              <span v-if="slopeFactor >= 0">&plus;</span>
              <span v-if="slopeFactor < 0">&minus;</span>
              <span class="var-coef" />
              <span>&times;</span>
              <span class="sym">Slope</span>
            </div>
          </div>
        </div>
        <v-row>
          <v-col cols="6">
            <div class="d-flex flex-column align-end">
              <div class="chart-type sofr">
                {{ niiAssetLabel }}
              </div>
            </div>
            <v-lazy
              :options="{threshold: 0.5}"
              transition="fade-transition"
            >
              <e-chart
                :ref="`echart-net-interest-income-sensitivity-sofr`"
                :autoresize="true"
                :option="sofrChartOptions"
                :style="`height: 230px;`"
                class="chart"
              />
            </v-lazy>
          </v-col>
          <v-col cols="6">
            <div class="d-flex flex-column align-end">
              <div class="chart-type slope">
                Slope
              </div>
            </div>
            <v-lazy
              :options="{threshold: 0.5}"
              transition="fade-transition"
            >
              <e-chart
                :ref="`echart-net-interest-income-sensitivity-slope`"
                :autoresize="true"
                :option="slopeChartOptions"
                :style="`height: 230px;`"
                class="chart"
              />
            </v-lazy>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </block-item-card>
</template>

  <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { use } from 'echarts/core'
import { LineChart, ScatterChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { toDecimalSignificant, toDecimalMark, computeYAxisBounds, computeXAxisBounds } from '@/lib/utils/helper'

use([LineChart, ScatterChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

export default {
  name      : 'NetInterestIncomeSensitivity',
  components: {
    BlockItemCard
  },
  dataStore: { FindBankRegressionData: 'Rates.FindBankRegressionData.Data' },
  mixins   : [chartsColorMixin],
  props    : {},
  data () {
    return {
      minBlockHeight: 350
    }
  },
  computed: {
    netInterestIncomeSensitivity () {
      return this.FindBankRegressionData?.bank?.netInterestIncomeSensitivity
    },
    niiAssetLabel () {
      return this.FindBankRegressionData?.bank?.netInterestIncomeSensitivity?.assetLabel || ''
    },
    sofr () {
      return this.netInterestIncomeSensitivity?.sofr || []
    },
    sofrFactor () {
      return this.netInterestIncomeSensitivity?.sofrFactor || 0
    },
    slope () {
      return this.netInterestIncomeSensitivity?.slope || []
    },
    slopeFactor () {
      return this.netInterestIncomeSensitivity?.slopeFactor || 0
    },
    slopeFactorAbs () {
      return Math.abs(this.netInterestIncomeSensitivity?.slopeFactor || 0)
    },

    slopeChartOptions () {
      return this.chartOps(this.slope, {
        name : 'Slope',
        color: 'synthesis-ui-yellow-01'
      })
    },
    sofrChartOptions () {
      return this.chartOps(this.sofr, {
        name : 'SOFR',
        color: 'synthesis-ui-red-02'
      })
    }
  },
  watch  : {},
  mounted () {},
  created () {},
  methods: {
    chartOps (values, { name, color }) {
      if (!values) return {}
      const totalY = [...values.amounts, ...values.samples.amounts]
      const totalX = [...values.rates, ...values.samples.rates]
      const y = computeYAxisBounds(totalY, 5)
      const x = computeXAxisBounds(totalX)
      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const [, amount] = params[0].value
            const axisValue = params[0].axisValue
            return `<div style="font-weight: bold; margin-bottom: 8px;">${axisValue}%</div>
                        <div style="margin: 4px 0;">
                            <span>${amount ? toDecimalSignificant(amount, 2) : 0}%</span>
                        </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 65,
          top         : 25,
          right       : 20,
          bottom      : 0,
          containLabel: true
        },
        xAxis: {
          type     : 'value',
          min      : x.min,
          max      : x.max,
          interval : 1,
          axisLabel: {
            margin   : 30,
            show     : true,
            color    : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize : 12,
            formatter: (value) => {
              return `${value}%`
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          min      : y.min,
          max      : y.max,
          interval : y.interval,
          axisLabel: {
            margin       : -55,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return `${toDecimalMark(value)}%`
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
                   name     : name,
                   type     : 'line',
                   data     : values.rates.map((v, idx) => [v, values.amounts[idx]]),
                   itemStyle: {
                     color: color.startsWith('#') ? color : this.themeColorRGBA(color, 1)
                   },
                   lineStyle: {
                     width: 3
                   },
                   symbol    : 'emptyCircle',
                   symbolSize: 0,
                   areaStyle : {
                     color: {
                       type      : 'linear',
                       x         : 0,
                       y         : 0,
                       x2        : 0,
                       y2        : 1,
                       colorStops: [
                         {
                           offset: 0.5,
                           color : this.themeColorRGBA(color, 0.3)
                         },
                         {
                           offset: 1,
                           color : this.themeColorRGBA(color, 0)
                         }
                       ]
                     }
                   }
                 },
                 {
                   name      : 'Samples',
                   type      : 'scatter',
                   data      : values.samples.rates.map((x, idx) => [x, values.samples.amounts[idx]]),
                   z         : 4,
                   clip      : true,
                   symbol    : 'circle',
                   symbolSize: 7,
                   itemStyle : { color: this.themeColorRGBA('synthesis-ui-blue-05') },
                   tooltip   : {
                     trigger  : 'item',
                     formatter: (p) => {
                       const [rate, amt] = p.value
                       return `<div style="font-weight:600;margin-bottom:6px;">${rate}%</div>
                    <div>${amt ? toDecimalSignificant(amt, 2) : 0}%</div>`
                     }
                   }
                 }]
      }
    },
    normalizeTicks (values) {
      const vals = values.map(Number)
      const minV = Math.min(...vals)
      const maxV = Math.max(...vals)

      const niceCeil = (x) => {
        if (x <= 0) return 0
        const p = Math.pow(10, Math.floor(Math.log10(x))) // power of 10
        const n = x / p // normalize to [1,10)
        const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10 // choose 1/2/5/10
        return m * p
      }

      // crosses zero → symmetric [-S, 0, +S]
      if (minV < 0 && maxV > 0) {
        const S = niceCeil(Math.max(Math.abs(minV), Math.abs(maxV)))
        return { min: -S, max: S, interval: S }
      }

      // all ≤ 0 → [-S, -S/2, 0]
      if (maxV <= 0) {
        const S = niceCeil(Math.abs(minV))
        return { min: -S, max: 0, interval: S / 2 }
      }

      // all ≥ 0 → [0, S/2, S]
      const S = niceCeil(maxV)
      return { min: 0, max: S, interval: S / 2 }
    }
  }
}
      </script>

      <style scoped>

    .gradient-grey-06-bg {
      background-color: var(--v-synthesis-ui-grey-06-base) !important;
    }

    .chart-title {
      font-weight: 600;
    }

    .chart-subtitle {
      margin-left: 8px;
      color: var(--v-synthesis-ui-grey-01-base);
      font-size: 10px;
      position: relative;
      top: 1px;
    }

    .metric-label {
      font-size: 12px;
      color: var(--v-synthesis-brand-dark-blue-base);
      margin-bottom: 2px;
    }

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 0 8px;
    }

    .chart-metric {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: -50px;
    }

    .metric-value {
      font-size: 12px;
      font-weight: 500;
    }

    .metric-value.positive {
      color: #4CAF50;
      font-size: 28px;
    }

    .metric-value.negative {
      color: #FF5722;
      font-size: 28px;
    }

    .chart-legend {
      display: flex;
      gap: 12px;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      color: #666;
    }

    .legend-color {
      width: 8px;
      height: 8px;
      border-radius: 2px;
    }

  .formula{
    display:flex; align-items:baseline; gap:5px;
    font-weight: 500;
  }
  .formula sub{
    font-size:.6em; line-height:0; vertical-align:baseline; position:relative; top:.25em;
    margin-left: -5px;
  }
  .var-const::before{ content: var(--const); }
  .var-coef::before{ content: var(--coef); }

.chart-type {
  font-size: 14px;
  font-weight: 400;
  color: white;
  border-radius: 4px;
  padding: 4px 12px;
  width: fit-content;
  margin-top: 20px;
  margin-right:4px;
}

.sofr{
  background-color: var(--v-synthesis-ui-red-05-base);
}

.slope {
  background-color: var(--v-synthesis-ui-yellow-01-base);
}

  </style>
